import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Search,
  Loader2,
  Play,
  X,
  Upload,
  FileText,
  Sparkles,
  Video,
  Filter,
  Grid,
  LayoutGrid,
  ChevronRight,
  Coins,
  BookOpen,
  AlertTriangle,
  CircleCheck,
  Clock,
  Film,
  Lock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function HowItWorks() {
  const [show, setShow] = useState(false);
  return (
    <div
      className="relative z-[60]"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <button
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted/80 hover:bg-muted text-xs font-medium text-muted-foreground hover:text-foreground transition-colors shrink-0"
        data-testid="how-it-works-btn"
      >
        <BookOpen className="w-3.5 h-3.5" />
        How it works
      </button>
      {show && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center pointer-events-none">
          <div className="bg-gray-900/95 text-white rounded-xl p-8 shadow-2xl backdrop-blur-sm max-w-2xl w-full pointer-events-auto">
            <h3 className="text-sm font-bold tracking-widest uppercase mb-6 text-gray-300">
              Transform Ideas Into High-Converting Ads
            </h3>
            <div className="grid grid-cols-3 gap-5">
              <div>
                <div className="rounded-lg overflow-hidden mb-3 aspect-square bg-gray-800">
                  <img src="https://static.prod-images.emergentagent.com/jobs/99c29390-be99-4853-b4ad-881ddec5b2ff/images/218ea995036b37aa7cf32b78d0ce0c13c7306b032d138d9ecb07a1d55b3790a6.png" alt="Pick a Style" className="w-full h-full object-cover" />
                </div>
                <p className="text-sm font-semibold text-gray-100 mb-1.5">Step 1: Pick a Style</p>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Browse our library and select the perfect look for your brand.
                </p>
              </div>
              <div>
                <div className="rounded-lg overflow-hidden mb-3 aspect-square bg-gray-800">
                  <img src="https://static.prod-images.emergentagent.com/jobs/99c29390-be99-4853-b4ad-881ddec5b2ff/images/21049a3972c8a3aa7013ddd209a4689a101063974b21e0910342fcf6dd744b2c.png" alt="Add Your Product" className="w-full h-full object-cover" />
                </div>
                <p className="text-sm font-semibold text-gray-100 mb-1.5">Step 2: Add Your Product</p>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Upload an image/Write your script
                </p>
              </div>
              <div>
                <div className="rounded-lg overflow-hidden mb-3 aspect-square bg-gray-800">
                  <img src="https://static.prod-images.emergentagent.com/jobs/99c29390-be99-4853-b4ad-881ddec5b2ff/images/faad008fda77168ce0f18b03a5e9436a135b2f0cb26da3bcb3cc1f069c2cd1a0.png" alt="See the Magic" className="w-full h-full object-cover" />
                </div>
                <p className="text-sm font-semibold text-gray-100 mb-1.5">Step 3: See the Magic</p>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Watch as we instantly transform your product into a stunning ad.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PRO_ONLY_TYPES = ["hero_shot", "ugc_avatar4"];

export default function TemplateGallery() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshCredits } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tag") || null;
  });
  const [availableTags, setAvailableTags] = useState([]);
  
  // Preview modal
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Recreation modal
  const [showRecreate, setShowRecreate] = useState(false);
  const [recreateTemplate, setRecreateTemplate] = useState(null);
  const [productFile, setProductFile] = useState(null);
  const [productPreview, setProductPreview] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [sceneTextOverlays, setSceneTextOverlays] = useState([]);
  const [sceneScripts, setSceneScripts] = useState([]);
  const [recreating, setRecreating] = useState(false);
  const [recreationProgress, setRecreationProgress] = useState(null);
  const [pricingMap, setPricingMap] = useState({});
  const [productAnalysis, setProductAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [suggestingScript, setSuggestingScript] = useState(false);

  const userRole = user?.role || "free";
  const isProOrAdmin = userRole === "pro" || userRole === "admin";

  // Read ?tag= from URL on mount and fetch templates
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tagParam = params.get("tag");
    if (tagParam) {
      setSelectedTag(tagParam);
    }
  }, [location.search]);

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTag]);

  useEffect(() => {
    // Fetch pricing config once
    axios.get(`${API}/api/credits/pricing`, { withCredentials: true })
      .then(res => {
        const map = {};
        (res.data.pricing || []).forEach(p => { map[p.action_key] = p.credits_cost; });
        setPricingMap(map);
      })
      .catch(() => {});
  }, []);

  // Auto-open modals when navigated from Dashboard with location state
  useEffect(() => {
    if (!loading && templates.length > 0 && location.state) {
      const { previewTemplateId, useTemplateId } = location.state;
      if (previewTemplateId) {
        const t = templates.find(tpl => tpl.id === previewTemplateId);
        if (t) handlePreview(t);
      } else if (useTemplateId) {
        const t = templates.find(tpl => tpl.id === useTemplateId);
        if (t) handleRecreate(t);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, templates, location.state]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedTag) {
        params.tag = selectedTag;
      }
      
      const response = await axios.get(`${API}/api/templates/published`, {
        params,
        withCredentials: true,
      });
      
      setTemplates(response.data.templates);
      setAvailableTags(response.data.available_tags || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const handleRecreate = (template) => {
    // Gate pro-only template types
    if (PRO_ONLY_TYPES.includes(template.template_type) && !isProOrAdmin) {
      setShowUpgradeModal(true);
      setShowPreview(false);
      return;
    }
    setRecreateTemplate(template);
    setProductFile(null);
    setProductPreview(null);
    setProductAnalysis(null);
    setAnalyzing(false);
    setInputValue("");
    setSceneTextOverlays(
      (template.template_type === "product_video" || template.template_type === "hero_shot")
        ? Array(template.scene_count || (template.template_type === "hero_shot" ? 1 : 2)).fill("")
        : []
    );
    setSceneScripts(
      template.template_type === "ugc_2scene"
        ? Array(template.scene_count || 2).fill("")
        : []
    );
    setRecreationProgress(null);
    setShowRecreate(true);
    setShowPreview(false);
  };

  const handleProductChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProductFile(file);
      setProductPreview(URL.createObjectURL(file));
      setProductAnalysis(null);

      // Upload temporarily to get URL for analysis
      const templateProductType = recreateTemplate?.product_type || "";
      if (templateProductType) {
        setAnalyzing(true);
        try {
          const formData = new FormData();
          formData.append("file", file);
          const uploadRes = await axios.post(`${API}/api/photoshoot/upload-product-image`, formData, {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          });
          const imageUrl = uploadRes.data.image_url;

          const analysisRes = await axios.post(`${API}/api/templates/analyze-product`, {
            image_url: imageUrl,
            template_product_type: templateProductType,
          }, { withCredentials: true });

          setProductAnalysis(analysisRes.data);
        } catch {
          // Silently fail — analysis is non-blocking
        } finally {
          setAnalyzing(false);
        }
      }
    }
  };

  const suggestScript = async () => {
    if (!recreateTemplate) return;
    setSuggestingScript(true);
    try {
      const isUGC2Scene = recreateTemplate.template_type === "ugc_2scene";
      const isSora2 = recreateTemplate.template_type === "ugc_sora2";
      const wordLimit = isSora2 ? (recreateTemplate.video_duration || 8) * 3 : 24;
      const sceneCount = isUGC2Scene ? (recreateTemplate.scene_count || 2) : 1;

      const res = await axios.post(`${API}/api/templates/suggest-script`, {
        template_type: recreateTemplate.template_type,
        word_limit: wordLimit,
        product_description: productAnalysis?.product_description || "",
        sample_script: recreateTemplate.script || "",
        scene_count: sceneCount,
      }, { withCredentials: true });

      if (isUGC2Scene && res.data.scripts) {
        setSceneScripts(res.data.scripts.slice(0, sceneCount));
      } else if (res.data.script) {
        setInputValue(res.data.script);
      }
      toast.success("Script suggested!");
    } catch (e) {
      toast.error("Failed to suggest script");
    } finally {
      setSuggestingScript(false);
    }
  };

  const startRecreation = async () => {
    if (!productFile) {
      toast.error("Please upload a product image");
      return;
    }

    const isProductVideo = recreateTemplate?.template_type === "product_video";
    const isHeroShot = recreateTemplate?.template_type === "hero_shot";
    const isUGC2Scene = recreateTemplate?.template_type === "ugc_2scene";

    if (isProductVideo) {
      const hasEmpty = sceneTextOverlays.some((t) => !t.trim());
      if (hasEmpty) {
        toast.error("Please fill in text overlays for all scenes");
        return;
      }
    } else if (isHeroShot) {
      // Hero shot only needs product image — no script/overlay validation
    } else if (isUGC2Scene) {
      const hasEmpty = sceneScripts.some((s) => !s.trim());
      if (hasEmpty) {
        toast.error("Please fill in scripts for all scenes");
        return;
      }
    } else {
      if (!inputValue.trim()) {
        toast.error("Please enter a script");
        return;
      }
      const wordCount = inputValue.trim().split(/\s+/).filter(w => w).length;
      const maxWords = recreateTemplate?.template_type === "ugc_sora2" ? (recreateTemplate?.video_duration || 8) * 3 : 24;
      if (wordCount > maxWords) {
        toast.error(`Script must be ${maxWords} words or less`);
        return;
      }
    }

    setRecreating(true);
    setRecreationProgress({ status: "starting", progress: 0, message: "Starting recreation..." });

    try {
      const formData = new FormData();
      formData.append("product_image", productFile);

      if (isProductVideo) {
        formData.append("text_overlays", JSON.stringify(sceneTextOverlays));
      } else if (isHeroShot) {
        // Hero shot — no additional fields, backend pulls text overlay from template
      } else if (isUGC2Scene) {
        formData.append("scene_scripts", JSON.stringify(sceneScripts));
      } else {
        formData.append("input_type", "script");
        formData.append("input_value", inputValue);
      }

      const response = await axios.post(
        `${API}/api/templates/${recreateTemplate.id}/recreate`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );

      const { recreation_id, job_id } = response.data;
      toast.info("Recreation started! This may take a few minutes.");

      // Poll for status
      pollRecreationStatus(recreation_id);
    } catch (error) {
      console.error("Error starting recreation:", error);
      toast.error(error.response?.data?.detail || "Failed to start recreation");
      setRecreating(false);
    }
  };

  const pollRecreationStatus = async (recreationId) => {
    let attempts = 0;
    const maxAttempts = 150; // ~12.5 minutes

    while (attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 5000));

      try {
        const response = await axios.get(
          `${API}/api/templates/recreations/${recreationId}/status?t=${Date.now()}`,
          {
            withCredentials: true,
            headers: { "Cache-Control": "no-cache" },
          }
        );

        const { status, progress, message, result, error, recreation } = response.data;

        setRecreationProgress({ status, progress, message });

        if (status === "completed") {
          toast.success("Video created successfully!");
          setRecreating(false);
          setShowRecreate(false);
          refreshCredits();
          // Navigate to a results page or show the video
          navigate(`/my-recreations/${recreationId}`);
          return;
        } else if (status === "failed") {
          toast.error(error || "Recreation failed");
          setRecreating(false);
          return;
        }
      } catch (error) {
        console.error("Error polling recreation status:", error);
      }

      attempts++;
    }

    toast.error("Recreation timed out");
    setRecreating(false);
  };

  const filteredTemplates = templates.filter((t) =>
    t.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.tags?.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-2">
                <LayoutGrid className="w-5 h-5 text-primary" />
                <h1 className="text-xl font-semibold">Video Templates</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {filteredTemplates.length} templates
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Search and Filters */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white"
              />
            </div>
            <HowItWorks />
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedTag === null ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedTag(null)}
              className="rounded-full"
            >
              All
            </Button>
            {availableTags.map((tag) => (
              <Button
                key={tag}
                variant={selectedTag && tag.toLowerCase().includes(selectedTag.toLowerCase()) ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedTag(tag)}
                className="rounded-full"
              >
                {tag}
              </Button>
            ))}
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-20">
            <Video className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-medium mb-2">No templates found</h3>
            <p className="text-muted-foreground">
              {searchQuery || selectedTag
                ? "Try adjusting your search or filters"
                : "Check back later for new templates"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                defaultCost={pricingMap.template_recreation}
                onPreview={() => handlePreview(template)}
                onRecreate={() => handleRecreate(template)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl">
          <div className="flex items-start justify-between">
            <DialogHeader className="flex-1">
              <DialogTitle>{previewTemplate?.title}</DialogTitle>
              <DialogDescription>{previewTemplate?.description}</DialogDescription>
            </DialogHeader>
            <HowItWorks />
          </div>
          
          <div className="space-y-4">
            {/* Video Player */}
            {previewTemplate?.sample_video && (
              <div className="aspect-[9/16] max-h-[60vh] mx-auto bg-black rounded-lg overflow-hidden">
                <video
                  src={previewTemplate.sample_video}
                  controls
                  autoPlay
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            
            {/* Tags */}
            <div className="flex flex-wrap gap-2">
              {previewTemplate?.tags?.map((tag) => (
                <Badge key={tag} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
            
            {/* Script Preview (UGC) or Scene Info (Product Video) */}
            {previewTemplate?.template_type === "product_video" ? (
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-1">
                  {previewTemplate.scene_count || 2} Scene Product Video
                </p>
                <p className="text-sm text-muted-foreground">
                  Recreate this with your own product image and text overlays
                </p>
              </div>
            ) : previewTemplate?.template_type === "ugc_2scene" ? (
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-1">
                  {previewTemplate.scene_count || 2} Scene UGC Video
                </p>
                <p className="text-sm text-muted-foreground">
                  Recreate this with your own product image and per-scene scripts
                </p>
              </div>
            ) : previewTemplate?.script ? (
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-1">Sample Script</p>
                <p className="text-sm text-muted-foreground italic">
                  "{previewTemplate.script}"
                </p>
              </div>
            ) : null}
            
            <Button onClick={() => handleRecreate(previewTemplate)} className="w-full" size="lg">
              <Sparkles className="w-4 h-4 mr-2" />
              Use This Template
              {(previewTemplate?.credits_cost || pricingMap.template_recreation) ? (
                <span className="ml-2 inline-flex items-center gap-1 bg-white/20 text-xs px-2 py-0.5 rounded-full">
                  <Coins className="w-3 h-3" />
                  {previewTemplate?.credits_cost || pricingMap.template_recreation}
                </span>
              ) : null}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recreation Modal - Two Column Layout */}
      <Dialog open={showRecreate} onOpenChange={(open) => !recreating && setShowRecreate(open)}>
        <DialogContent className="max-w-5xl p-0 overflow-y-auto max-h-[90vh] gap-0">
          {recreating ? (
            <div className="py-16 text-center px-8">
              <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary mb-4" />
              <h3 className="text-lg font-medium mb-2">Creating Your Video</h3>
              <p className="text-muted-foreground mb-4">{recreationProgress?.message}</p>
              <div className="w-full max-w-xs mx-auto bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="bg-primary h-full transition-all duration-500"
                  style={{ width: `${recreationProgress?.progress || 0}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {recreationProgress?.progress || 0}% complete
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 min-h-[520px]">
              {/* Left Panel - Video Preview */}
              <div className="bg-slate-50 flex items-center justify-center p-0 relative border-r border-border">
                {recreateTemplate?.sample_video ? (
                  <video
                    src={recreateTemplate.sample_video}
                    controls
                    className="w-full h-full object-contain max-h-[250px] md:max-h-[520px]"
                    data-testid="recreate-video-preview"
                  />
                ) : recreateTemplate?.composite_image ? (
                  <img
                    src={recreateTemplate.composite_image}
                    alt="Template preview"
                    className="w-full h-full object-contain max-h-[250px] md:max-h-[520px]"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <Video className="w-16 h-16 mb-2" />
                    <p className="text-sm">No preview available</p>
                  </div>
                )}
              </div>

              {/* Right Panel - Form */}
              <div className="p-6 flex flex-col overflow-y-auto md:max-h-[520px]">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-lg font-bold tracking-tight">ADD YOUR PRODUCT INFO</h2>
                  <HowItWorks />
                </div>
                <p className="text-xs text-muted-foreground mb-5">
                  Using: {recreateTemplate?.title}
                  {recreateTemplate?.template_type === "product_video" && (
                    <span> ({recreateTemplate?.scene_count || 2} scene{(recreateTemplate?.scene_count || 2) > 1 ? 's' : ''})</span>
                  )}
                </p>

                <div className="flex-1 space-y-5">
                  {/* Product Image Upload */}
                  <div>
                    <Label className="text-sm font-medium">Product Image *</Label>
                    <div
                      className={`mt-2 border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-all ${
                        productPreview
                          ? "border-primary/50 bg-primary/5"
                          : "border-border hover:border-primary/30"
                      }`}
                      onClick={() => document.getElementById("product-upload-recreate").click()}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const file = e.dataTransfer?.files?.[0];
                        if (file && file.type.startsWith("image/")) {
                          const syntheticEvent = { target: { files: [file] } };
                          handleProductChange(syntheticEvent);
                        }
                      }}
                      data-testid="product-upload-zone"
                    >
                      {productPreview ? (
                        <img
                          src={productPreview}
                          alt="Product"
                          className="max-h-28 mx-auto rounded-lg"
                        />
                      ) : (
                        <div className="space-y-1.5">
                          <Upload className="w-7 h-7 mx-auto text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">Drag and drop or click to upload</p>
                          <p className="text-xs text-muted-foreground/60">Max 10MB for uploaded photo</p>
                        </div>
                      )}
                      <input
                        id="product-upload-recreate"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleProductChange}
                      />
                    </div>

                  {/* Product Analysis Feedback */}
                  {analyzing && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2" data-testid="product-analyzing">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Analyzing product image...
                    </div>
                  )}
                  {productAnalysis && !productAnalysis.category_match && productAnalysis.mismatch_message && (
                    <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2.5" data-testid="product-mismatch-warning">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                      <span style={{ whiteSpace: "pre-line" }}>{productAnalysis.mismatch_message}{productAnalysis.product_description && <span className="block mt-0.5 text-amber-600/70">Detected: {productAnalysis.product_description}</span>}</span>
                    </div>
                  )}
                  {productAnalysis && !productAnalysis.quality_ok && productAnalysis.quality_warnings?.length > 0 && (
                    <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2.5" data-testid="product-quality-warning">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                      <span>{productAnalysis.quality_warnings[0]}</span>
                    </div>
                  )}
                  {productAnalysis && productAnalysis.category_match && productAnalysis.quality_ok && (
                    <div className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-3 py-2.5" data-testid="product-analysis-success">
                      <CircleCheck className="w-4 h-4 shrink-0 text-emerald-500" />
                      <span>Product looks great for this template!{productAnalysis.product_description && <span className="text-emerald-600/70 ml-1">Detected: {productAnalysis.product_description}</span>}</span>
                    </div>
                  )}
                  </div>

                  {recreateTemplate?.template_type === "hero_shot" ? null : recreateTemplate?.template_type === "product_video" ? (
                    <div>
                      <Label className="text-sm font-medium">
                        Text Overlay{(recreateTemplate?.scene_count || 1) > 1 ? 's' : ''} ({recreateTemplate?.scene_count || (recreateTemplate?.template_type === "hero_shot" ? 1 : 2)} scene{(recreateTemplate?.scene_count || 1) > 1 ? 's' : ''})
                      </Label>
                      <div className="space-y-2.5 mt-2">
                        {sceneTextOverlays.map((overlay, idx) => (
                          <div key={idx}>
                            <Label className="text-xs text-muted-foreground">Scene {idx + 1}</Label>
                            <Input
                              value={overlay}
                              onChange={(e) => {
                                const arr = [...sceneTextOverlays];
                                arr[idx] = e.target.value;
                                setSceneTextOverlays(arr);
                              }}
                              placeholder={`Text overlay for scene ${idx + 1}...`}
                              data-testid={`scene-overlay-input-${idx}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : recreateTemplate?.template_type === "ugc_2scene" ? (
                    <div>
                      <Label className="text-sm font-medium">
                        Scripts ({recreateTemplate?.scene_count || 2} scenes)
                      </Label>
                      <div className="space-y-2.5 mt-2">
                        {sceneScripts.map((script, idx) => (
                          <div key={idx}>
                            <Label className="text-xs text-muted-foreground">Scene {idx + 1}</Label>
                            <Textarea
                              value={script}
                              onChange={(e) => {
                                const words = e.target.value.trim().split(/\s+/).filter(w => w);
                                if (words.length <= 24 || e.target.value.length < script.length) {
                                  const arr = [...sceneScripts];
                                  arr[idx] = e.target.value;
                                  setSceneScripts(arr);
                                }
                              }}
                              placeholder={`Script for scene ${idx + 1}...`}
                              className="min-h-[70px]"
                              data-testid={`scene-script-input-${idx}`}
                            />
                            <p className={`text-xs mt-0.5 ${(script || "").trim().split(/\s+/).filter(w => w).length > 20 ? "text-destructive" : "text-muted-foreground"}`}>
                              {(script || "").trim().split(/\s+/).filter(w => w).length}/24 words
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Textarea
                        value={inputValue}
                        onChange={(e) => {
                          const maxWords = recreateTemplate?.template_type === "ugc_sora2" ? (recreateTemplate?.video_duration || 8) * 3 : 24;
                          const words = e.target.value.trim().split(/\s+/).filter(w => w);
                          if (words.length <= maxWords || e.target.value.length < inputValue.length) {
                            setInputValue(e.target.value);
                          }
                        }}
                        placeholder="e.g., This body lotion changed my skin! So smooth and hydrated every single day."
                        className="mt-2 min-h-[90px]"
                        data-testid="recreation-script-textarea"
                      />
                      {(() => {
                        const maxWords = recreateTemplate?.template_type === "ugc_sora2" ? (recreateTemplate?.video_duration || 8) * 3 : 24;
                        const currentWords = inputValue.trim().split(/\s+/).filter(w => w).length;
                        return (
                          <p className={`text-xs mt-1.5 ${currentWords > maxWords ? "text-destructive" : "text-muted-foreground"}`}>
                            {currentWords}/{maxWords} words
                            {recreateTemplate?.template_type === "ugc_sora2" && <span className="ml-1">({recreateTemplate?.video_duration}s @ 3 wps)</span>}
                          </p>
                        );
                      })()}
                    </div>
                  )}
                  {/* Suggest Script Button — shown for all UGC types */}
                  {recreateTemplate?.template_type !== "product_video" && recreateTemplate?.template_type !== "hero_shot" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={suggestScript}
                      disabled={suggestingScript || recreating || !productFile}
                      className="mt-2"
                      data-testid="suggest-script-btn"
                      title={!productFile ? "Upload a product image first" : ""}
                    >
                      {suggestingScript ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                      {suggestingScript ? "Generating..." : "Suggest Script"}
                    </Button>
                  )}
                </div>

                {/* Generate Button */}
                <Button
                  onClick={startRecreation}
                  disabled={
                    !productFile ||
                    (recreateTemplate?.template_type === "product_video"
                      ? sceneTextOverlays.some((t) => !t.trim())
                      : recreateTemplate?.template_type === "hero_shot"
                        ? false
                        : recreateTemplate?.template_type === "ugc_2scene"
                          ? sceneScripts.some((s) => !s.trim())
                          : !inputValue.trim())
                  }
                  className="w-full mt-5 h-12 text-base font-semibold"
                  size="lg"
                  data-testid="start-recreation-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Video
                  {(recreateTemplate?.credits_cost || pricingMap.template_recreation) ? (
                    <span className="ml-2 inline-flex items-center gap-1 bg-white/20 text-xs px-2 py-0.5 rounded-full">
                      <Coins className="w-3 h-3" />
                      {recreateTemplate?.credits_cost || pricingMap.template_recreation}
                    </span>
                  ) : null}
                </Button>
                <p className="text-xs text-muted-foreground/60 text-center mt-3">AI can make mistakes, so please check the video</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upgrade to Pro Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="max-w-sm text-center" data-testid="upgrade-modal">
          <div className="py-4">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-amber-600" />
            </div>
            <DialogTitle className="text-xl font-bold mb-2">Pro Feature</DialogTitle>
            <p className="text-sm text-muted-foreground mb-6">
              This template is available exclusively for Pro users. Upgrade your plan to unlock Hero Shots, Avatar Videos, and more.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => setShowUpgradeModal(false)}>
                Maybe later
              </Button>
              <Button className="flex-1 rounded-full" onClick={() => { setShowUpgradeModal(false); navigate("/pricing"); }} data-testid="upgrade-cta-btn">
                Upgrade to Pro
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Template Card Component
function TemplateCard({ template, defaultCost, onPreview, onRecreate }) {
  const videoRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const cost = template.credits_cost;

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="group bg-white rounded-xl overflow-hidden border border-border shadow-sm hover:shadow-xl transition-all duration-300"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Thumbnail */}
      <div className="aspect-[9/16] relative bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
        {PRO_ONLY_TYPES.includes(template.template_type) && (
          <div className="absolute top-2 right-2 z-10 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid={`pro-badge-${template.id}`}>
            PRO
          </div>
        )}
        {template.sample_video ? (
          <>
            <video
              ref={videoRef}
              src={template.sample_video}
              className="w-full h-full object-cover"
              loop
              playsInline
            />
            {!isHovering && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                  <Play className="w-6 h-6 text-slate-700 ml-1" />
                </div>
              </div>
            )}
          </>
        ) : template.composite_image ? (
          <img
            src={template.composite_image}
            alt={template.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Video className="w-12 h-12 text-muted-foreground" />
          </div>
        )}

        {/* Hover overlay */}
        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-4 transition-opacity duration-300 ${
            isHovering ? "opacity-100" : "opacity-0"
          }`}
        >
          <Button onClick={onRecreate} className="w-full mb-2" size="sm">
            <Sparkles className="w-4 h-4 mr-2" />
            Use Template
          </Button>
          <Button onClick={onPreview} variant="secondary" className="w-full" size="sm">
            Preview
          </Button>
        </div>
      </div>

      {/* Info bar */}
      <div className="p-3 flex items-center justify-between text-xs text-muted-foreground border-t border-border">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          ~{template.video_duration || (template.scene_count ? template.scene_count * 5 : template.template_type === 'ugc_2scene' ? 12 : template.template_type === 'ugc_sora2' ? 12 : 6)}s
        </span>
        <span className="flex items-center gap-1">
          <Film className="w-3 h-3" />
          {template.scene_count || (template.template_type === 'ugc_2scene' ? 2 : 1)} scene{(template.scene_count || (template.template_type === 'ugc_2scene' ? 2 : 1)) > 1 ? 's' : ''}
        </span>
        {cost > 0 && (
          <span className="flex items-center gap-1 text-amber-600 font-medium" data-testid="template-credit-cost">
            <Coins className="w-3 h-3" />
            {cost}
          </span>
        )}
      </div>
    </div>
  );
}
